import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from './ErrorFallback.tsx'
import { initFirebase } from './lib/firebase'

// Wallet crypto libs expect Node's Buffer.
// Vite doesn't polyfill it by default in the browser, so we provide it.
import { Buffer } from 'buffer'
const g = globalThis as any
g.Buffer = g.Buffer ?? Buffer

// Some dependencies also expect Node's `process` global.
g.process = g.process ?? { env: {} }
g.process.env = g.process.env ?? {}
g.process.env.NODE_ENV = g.process.env.NODE_ENV ?? import.meta.env.MODE

import "./main.css"
import "./styles/theme.css"
import "./index.css"

async function bootstrap() {
  const rootEl = document.getElementById('root')
  if (!rootEl) {
    throw new Error('Missing #root element')
  }

  const root = createRoot(rootEl)
  let fatalRendered = false

  const renderFatal = (e: unknown) => {
    if (fatalRendered) return
    fatalRendered = true
    const err = e instanceof Error ? e : new Error(String(e))
    console.error('[FATAL ERROR]', err.message, err.stack)
    root.render(
      <ErrorFallback
        error={err}
        resetErrorBoundary={() => window.location.reload()}
      />
    )
  }

  // Make sure the user sees *something* immediately, even if init fails.
  root.render(
    <div style={{ padding: 16, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif' }}>
      Loading…
    </div>
  )

  // Catch errors that happen outside React rendering.
  window.addEventListener('error', (ev) => {
    renderFatal((ev as ErrorEvent).error ?? (ev as ErrorEvent).message)
  })
  window.addEventListener('unhandledrejection', (ev) => {
    renderFatal((ev as PromiseRejectionEvent).reason)
  })

  let firebaseReady = true
  try {
    await initFirebase()
  } catch (e) {
    firebaseReady = false
    console.warn('[BOOT] Firebase init skipped:', e)
  }

  try {
    const { default: App } = await import('./App.tsx')
    root.render(
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <App />
      </ErrorBoundary>
    )
    if (!firebaseReady) {
      console.info('[BOOT] App started without Firebase config; using local fallbacks.')
    }
  } catch (e) {
    renderFatal(e)
  }
}

bootstrap()
